import React, { FunctionComponent, useMemo, useCallback } from "react";
import { Theme, makeStyles, Grid } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "../../../../components/buttons/primary-button";
import { PresentationNavButton } from "../components/presentation-nav-button";
import { ArrowLeftIcon } from "../../../../assets/icons/arrows/arrow-left-icon";
import { PresentationTextField } from "../components/presentation-textfield";
import { usePassword } from "../../../../hooks/use-password";
import { useUpdatePasswordMutation } from "../../../../api/smartmelts-api/graphql/generated";
import { SnackbarSeverity, useSnackbar } from "../../../../components/snackbar/snackbar-context";
import { getGraphqlErrorLocalized } from "../../../../api/errors/graphql-error-handler";
import { HeadingText } from "../../../../components/text/heading-text";
import { SubtitleText } from "../../../../components/text/subtitle-text";

const useStyles = makeStyles((theme: Theme) => ({
  subheading: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(2),
  },
  saveButtonContainer: {
    marginTop: theme.spacing(5),
    paddingRight: theme.spacing(4),
  },
  saveButton: {
    width: "100%",
    height: "100%",
  },
}));

interface IUserUpdatePasswordProps {
  onReturn: () => void;
}

export const UserUpdatePassword: FunctionComponent<IUserUpdatePasswordProps> = (props) => {
  const { onReturn } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const {
    password: oldPassword,
    isPasswordValid: isOldPasswordValid,
    onPasswordChange: onOldPasswordChange,
  } = usePassword("");
  const { password, isPasswordValid, onPasswordChange } = usePassword("");
  const { password: passwordRepeat, onPasswordChange: onPasswordRepeatChange, isPasswordSame } = usePassword(
    "",
    password,
  );

  const canSubmit = useMemo(() => isOldPasswordValid && isPasswordValid && isPasswordSame, [
    isOldPasswordValid,
    isPasswordValid,
    isPasswordSame,
  ]);

  const [updatePasswordMutation, { loading }] = useUpdatePasswordMutation({
    onCompleted: (data) => {
      if (data.updatePassword.success) {
        showSnackbar(t("user_data.change_password.messages.password_updated"), SnackbarSeverity.SUCCESS);
        onReturn();
      }
    },
    onError: (error) => {
      showSnackbar(getGraphqlErrorLocalized(t, "update_password", error), SnackbarSeverity.ERROR);
    },
  });

  const updatePassword = useCallback(() => {
    updatePasswordMutation({
      variables: {
        oldPassword: oldPassword,
        newPassword: password,
      },
    });
  }, [oldPassword, password, updatePasswordMutation]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <HeadingText>{t("user_data.change_password.header")}</HeadingText>
      </Grid>
      <Grid item xs={12} className={classes.subheading}>
        <SubtitleText>{t("user_data.change_password.subtitle")}</SubtitleText>
      </Grid>

      <Grid item xs={12} sm={6}>
        <PresentationTextField
          fullWidth
          isPassword
          label={t("user_data.change_password.old_password")}
          value={oldPassword}
          onChange={(event) => onOldPasswordChange(event.target.value)}
          autoFocus
        />
      </Grid>
      <Grid item xs={12} sm={6} />
      <Grid item xs={12} sm={6}>
        <PresentationTextField
          fullWidth
          isPassword
          label={t("user_data.change_password.new_password")}
          value={password}
          onChange={(event) => onPasswordChange(event.target.value)}
          error={!!password.length && !isPasswordValid}
          errorMessage={t("login.errors.password_too_short")}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <PresentationTextField
          fullWidth
          isPassword
          label={t("user_data.change_password.confirm_new_password")}
          value={passwordRepeat}
          onChange={(event) => onPasswordRepeatChange(event.target.value)}
          error={!!passwordRepeat.length && !!passwordRepeat && !isPasswordSame}
          errorMessage={t("login.errors.passwords_dont_match")}
        />
      </Grid>
      <Grid container item xs={12} spacing={3} className={classes.saveButtonContainer}>
        <Grid item>
          <PresentationNavButton onClick={onReturn}>
            <ArrowLeftIcon width={24} />
          </PresentationNavButton>
        </Grid>
        <Grid item>
          <PrimaryButton
            onClick={updatePassword}
            loading={loading}
            disabled={!canSubmit}
            className={classes.saveButton}
          >
            {t("user_data.change_password.button")}
          </PrimaryButton>
        </Grid>
      </Grid>
    </Grid>
  );
};
